import { useI18n } from '@ecom/i18n';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx } from '../utils/cx';

export type ButtonVariant = 'primary' | 'secondary' | 'danger';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  readonly children: ReactNode;
}

/**
 * Primary interactive control, styled from the prototypes' `.pop`/`.accent`
 * treatment (souk-storefront.html "Confirmer la commande",
 * souk-dashboard.html primary actions).
 *
 * Accessibility: a loading button keeps its accessible name (the label text
 * stays in the DOM, `aria-busy` is set) rather than replacing it with a bare
 * spinner — a screen reader user must still hear what the button does while
 * it is busy, not just that something, unnamed, is happening.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref,
) {
  const { t } = useI18n();
  const isDisabled = disabled === true || loading;

  return (
    <button
      ref={ref}
      type="button"
      className={cx(
        'ecom-button',
        `ecom-button--${variant}`,
        size === 'sm' && 'ecom-button--sm',
        className,
      )}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <svg
          className="ecom-button__spinner"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      ) : null}
      <span>{children}</span>
      {loading ? <span className="ecom-sr-only">{t('common.loading')}</span> : null}
    </button>
  );
});
